import type { ApiClient } from "@talpipe/api"
import type { ApiResponse } from "@talpipe/types"
import type { PasswordResetOtpVerifyPayload, TalentSession } from "~/types/auth"
import type { Talent } from "~/types/profile/talent"
import type {
  ForgotPasswordInput,
  ResetPasswordInput,
  SignInInput,
  SignUpInput,
  VerifyPasswordResetOtpInput,
} from "~/validation/auth"

export async function fetchTalentMe(api: ApiClient): Promise<Talent | null> {
  const res = await api.get<ApiResponse<Talent | null>>("/auth/me")

  if (!res.successful || res.data == null) {
    throw new Error(res.message || "Failed to fetch talent.")
  }

  return res.data
}

export async function registerTalent(api: ApiClient, values: SignUpInput): Promise<TalentSession> {
  const res = await api.post<ApiResponse<TalentSession>>("/auth/register", {
    body: {
      email: values.email,
      password: values.password,
      passwordConfirmation: values.passwordConfirmation,
      firstName: values.firstName,
      lastName: values.lastName,
    },
  })

  if (!res.successful || !res.data?.accessToken) {
    throw new Error(res.message || "Registration failed.")
  }

  return res.data
}

export async function verifyTalentEmail(api: ApiClient, token: string): Promise<TalentSession> {
  const res = await api.get<ApiResponse<TalentSession>>(
    `/auth/email/verify/${encodeURIComponent(token)}`,
  )

  if (!res.successful || !res.data?.accessToken) {
    throw new Error(res.message || "Email verification failed.")
  }

  return res.data
}

export async function signInTalent(api: ApiClient, values: SignInInput): Promise<TalentSession> {
  const res = await api.post<ApiResponse<TalentSession>>("/auth/login", {
    body: {
      email: values.email,
      password: values.password,
    },
  })

  if (!res.successful || !res.data?.accessToken) {
    throw new Error(res.message || "Sign in failed.")
  }

  return res.data
}

export async function resendVerificationEmail(api: ApiClient, email: string): Promise<string> {
  const res = await api.post<ApiResponse<{ message?: string } | null>>(
    "/auth/email/resend-verification",
    {
      body: { email },
    },
  )

  if (!res.successful) {
    throw new Error(res.message || "Failed to resend verification email.")
  }

  return res.message || res.data?.message || "Verification email sent."
}

export async function forgotPassword(api: ApiClient, values: ForgotPasswordInput): Promise<string> {
  const res = await api.post<ApiResponse<{ message?: string } | null>>("/auth/password/otp/send", {
    body: {
      email: values.email,
    },
  })

  if (!res.successful) {
    throw new Error(res.message || "Failed to send password reset link.")
  }

  return res.message || res.data?.message || "Password reset link sent."
}

export async function verifyPasswordResetOtp(
  api: ApiClient,
  values: VerifyPasswordResetOtpInput,
): Promise<string> {
  const res = await api.post<ApiResponse<PasswordResetOtpVerifyPayload>>(
    "/auth/password/otp/verify",
    {
      body: {
        email: values.email,
        otp: values.otp,
      },
    },
  )

  if (!res.successful || !res.data?.resetToken) {
    throw new Error(res.message || "Failed to verify reset code.")
  }

  return res.data.resetToken
}

export async function resetPassword(api: ApiClient, values: ResetPasswordInput): Promise<string> {
  const res = await api.post<ApiResponse<null>>("/auth/password/reset", {
    body: {
      email: values.email,
      resetToken: values.resetToken,
      password: values.password,
      passwordConfirmation: values.passwordConfirmation,
    },
  })

  if (!res.successful) {
    throw new Error(res.message || "Failed to reset password.")
  }

  return res.message || "Password reset successfully."
}

export async function logoutTalent(api: ApiClient): Promise<string> {
  const res = await api.post<ApiResponse<null>>("/auth/logout")

  if (!res.successful) {
    throw new Error(res.message || "Failed to sign out.")
  }

  return res.message || "Logged out successfully."
}

export async function completeGoogleAuth(
  api: ApiClient,
  code: string,
  state: string,
): Promise<TalentSession> {
  const res = await api.get<ApiResponse<TalentSession>>("/auth/google/callback", {
    query: {
      code,
      state,
    },
  })

  if (!res.successful || !res.data?.accessToken) {
    throw new Error(res.message || "Google sign in failed.")
  }

  return res.data
}

/**
 * Binds an {@link ApiClient} once.
 */
export function createAuthService(api: ApiClient) {
  return {
    fetchTalentMe: () => fetchTalentMe(api),
    registerTalent: (values: SignUpInput) => registerTalent(api, values),
    verifyTalentEmail: (token: string) => verifyTalentEmail(api, token),
    signInTalent: (values: SignInInput) => signInTalent(api, values),
    resendVerificationEmail: (email: string) => resendVerificationEmail(api, email),
    forgotPassword: (values: ForgotPasswordInput) => forgotPassword(api, values),
    verifyPasswordResetOtp: (values: VerifyPasswordResetOtpInput) =>
      verifyPasswordResetOtp(api, values),
    resetPassword: (values: ResetPasswordInput) => resetPassword(api, values),
    completeGoogleAuth: (code: string, state: string) => completeGoogleAuth(api, code, state),
    logoutTalent: () => logoutTalent(api),
  }
}
